import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Box, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';
import { tokens } from '../../theme';
import { useContentPlayer } from '../../context/ContentPlayerContext';
import ContentPlayer from '../Player/ContentPlayer';
import { learnerService, userService } from '../../services/api';
import { validateSearchQuery } from '../../utils/searchValidation';
import ContentSearchMessage, { searchContent, GapComment, seekLabel } from '../common/ContentSearch';
import { useToast } from '../../context/ToastContext';
import ThinkingIndicator from './ThinkingIndicator';
import EmptyState from '../common/EmptyState';
import { useNavigate } from 'react-router-dom';
// ─────────────────────────────────────────────────────────────────────────────
// LearnerDashboard — search-first learner landing.
//
// VISUAL: package design (required-training cards, recommendation grid, skill
//   onboarding, inline answer card) — all on design-system tokens.
// LOGIC:  the real dashboard's wiring is preserved verbatim and underneath the
//   new visuals. SAFETY-CRITICAL behavior NOT changed:
//     - inline search via /api/contents/search (NOT navigation to a route)
//     - search query validation (validateSearchQuery)
//     - content-player open + pick tracking (the player enrols when content plays, N4 pt 1 / N40.1 pt 5)
//     - Yes / No / Skip feedback -> /api/user-queries/feedback (only an explicit No is unresolved, N4 pt 3)
//     Removed 10 Oct 2026 (N4 pt 3): the automatic No after 2 minutes idle, on tab-leave and on page close,
//     and after the same search 4 times in 10 minutes. A learner who read the answer and left was counted as
//     unresolved; no feedback now counts as resolved.
//     - recent-search history
//   These feed the Insights analytics loop and must remain intact.
// ─────────────────────────────────────────────────────────────────────────────

// ── Search bar (package visual, inline-submit) ────────────────────────────────
const SearchBar = ({ value, onChange, onSubmit, onFocus, showClear, onClear, containerRef }) => {
    const inputRef = useRef(null);
    return (
        <Box
            ref={containerRef}
            sx={{
                display: 'flex', alignItems: 'center', gap: '12px',
                background: tokens.color.bgPrimary,
                border: `0.5px solid ${tokens.color.borderSecondary}`,
                borderRadius: tokens.radius.lg,
                padding: '12px 18px', marginBottom: '32px', cursor: 'text',
                transition: `border-color ${tokens.transition}, box-shadow ${tokens.transition}`,
                '&:focus-within': {
                    borderColor: tokens.color.borderPrimary,
                    boxShadow: `0 0 0 3px rgba(17,17,16,0.06)`,
                },
            }}
            onClick={() => inputRef.current?.focus()}
        >
                <Box
                component="input"
                ref={inputRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={onFocus}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSubmit(); } }}
                placeholder="Ask anything…"
                sx={{
                    flex: 1, border: 'none', outline: 'none',
                    fontSize: tokens.font.size.md, color: tokens.color.textPrimary,
                    background: 'transparent', fontFamily: tokens.font.family,
                    '&::placeholder': { color: tokens.color.textTertiary },
                }}
            />
            <Box
                component="button"
                type="button"
                onClick={(e) => { e.stopPropagation(); onSubmit(); }}
                sx={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <Search sx={{ fontSize: '18px', color: tokens.color.textTertiary, flexShrink: 0 }} />
            </Box>
            {showClear && (
                <Box
                    component="button"
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onClear(); }}
                    sx={{
                        fontSize: tokens.font.size.xs, fontWeight: tokens.font.weight.medium,
                        color: tokens.color.textSecondary, background: tokens.color.bgSecondary,
                        border: `0.5px solid ${tokens.color.borderTertiary}`,
                        borderRadius: tokens.radius.sm, padding: '4px 10px', cursor: 'pointer',
                        fontFamily: tokens.font.family, flexShrink: 0,
                        transition: `all ${tokens.transition}`,
                        '&:hover': { background: tokens.color.bgTertiary, color: tokens.color.textPrimary },
                    }}
                >
                    Clear
                </Box>
            )}
        </Box>
    );
};

// ── Required training card (package visual) ───────────────────────────────────
const RequiredCard = ({ course, onClick }) => {
    const isOverdue = course.dueDate?.toLowerCase().includes('overdue');
    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex', alignItems: 'center', gap: '16px',
                background: tokens.color.bgPrimary,
                border: `0.5px solid ${tokens.color.borderTertiary}`,
                borderLeft: `3px solid ${tokens.color.alertDot}`,
                borderRadius: tokens.radius.md, padding: '16px 20px',
                marginBottom: '10px', cursor: 'pointer',
                transition: `background ${tokens.transition}`,
                '&:hover': { background: tokens.color.bgSecondary },
            }}
        >
            <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: tokens.font.size.md, fontWeight: tokens.font.weight.medium, color: tokens.color.textPrimary, marginBottom: '6px', fontFamily: tokens.font.family }}>
                    {course.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Box sx={{ fontSize: tokens.font.size.xs, fontWeight: tokens.font.weight.medium, color: tokens.color.alertDot, background: tokens.color.status.alertBg, padding: '2px 9px', borderRadius: tokens.radius.sm, letterSpacing: '0.02em' }}>
                        Required
                    </Box>
                    {course.dueDate && (
                        <Typography sx={{ fontSize: tokens.font.size.xs, fontWeight: tokens.font.weight.medium, color: isOverdue ? tokens.color.alertDot : tokens.color.warning, fontFamily: tokens.font.family }}>
                            {course.dueDate}
                        </Typography>
                    )}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                <Box sx={{ width: '80px', height: '5px', background: tokens.color.bgTertiary, borderRadius: '3px', position: 'relative' }}>
                    <Box sx={{ position: 'absolute', left: 0, top: 0, height: '5px', width: `${course.progress || 0}%`, borderRadius: '3px', background: tokens.color.textPrimary, opacity: 0.75 }} />
                </Box>
                <Box sx={{ fontSize: tokens.font.size.xs, fontWeight: tokens.font.weight.medium, color: tokens.color.textPrimary, background: tokens.color.bgSecondary, border: `0.5px solid ${tokens.color.borderTertiary}`, borderRadius: tokens.radius.sm, padding: '5px 14px', cursor: 'pointer', fontFamily: tokens.font.family, whiteSpace: 'nowrap' }}>
                    {(course.progress || 0) > 0 ? 'Continue' : 'Start'}
                </Box>
            </Box>
        </Box>
    );
};

// ── All-required-done banner (package visual) ─────────────────────────────────
const AllDoneBanner = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', background: tokens.color.status.activeBg, border: `0.5px solid ${tokens.color.status.activeText}`, borderRadius: tokens.radius.md, padding: '12px 18px', marginBottom: '28px' }}>
        <Box sx={{ width: '16px', height: '16px', borderRadius: '50%', border: `1.2px solid ${tokens.color.status.activeText}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '10px', color: tokens.color.status.activeText, fontWeight: 600 }}>
            ✓
        </Box>
        <Typography sx={{ fontSize: tokens.font.size.md, fontWeight: tokens.font.weight.medium, color: tokens.color.status.activeText, fontFamily: tokens.font.family }}>
            All required training complete
        </Typography>
    </Box>
);

// ── Recommendation card (package visual) ──────────────────────────────────────
const RecCard = ({ module, onClick }) => {
    const isVideo = module.type === 'video' || module.duration?.includes('Video');
    return (
        <Box
            onClick={onClick}
            sx={{
                background: tokens.color.bgPrimary,
                border: `0.5px solid ${tokens.color.borderTertiary}`,
                borderRadius: tokens.radius.lg, overflow: 'hidden', cursor: 'pointer',
                transition: `border-color ${tokens.transition}`,
                '&:hover': { borderColor: tokens.color.borderSecondary },
            }}
        >
            {/* Thumbnail removed — text-forward recommendation card. */}
            <Box sx={{ padding: '14px' }}>
                {module.isMandatory && (
                    <Box component="span" sx={{ display: 'inline-block', fontSize: '10px', fontWeight: tokens.font.weight.medium, color: tokens.color.brand, background: tokens.color.brandLight, borderRadius: tokens.radius.sm, padding: '2px 8px', marginBottom: '6px', fontFamily: tokens.font.family, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Required
                    </Box>
                )}
                {module.skill && (
                    <Typography sx={{ fontSize: tokens.font.size.xs, color: tokens.color.textSecondary, marginBottom: '5px', fontFamily: tokens.font.family }}>
                        Matches: {module.skill}
                    </Typography>
                )}
                <Typography sx={{ fontSize: tokens.font.size.md, fontWeight: tokens.font.weight.medium, color: tokens.color.textPrimary, marginBottom: '3px', fontFamily: tokens.font.family }}>
                    {module.name}
                </Typography>
                <Typography sx={{ fontSize: tokens.font.size.xs, color: tokens.color.textTertiary, fontFamily: tokens.font.family }}>
                    {module.duration}
                </Typography>
            </Box>
        </Box>
    );
};

// ── Section label (package visual) ────────────────────────────────────────────
const SectionLabel = ({ children, sub, subAction }) => (
    <Box sx={{ marginBottom: '14px' }}>
        <Typography sx={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, color: tokens.color.textPrimary, fontFamily: tokens.font.family, letterSpacing: '-0.01em' }}>
            {children}
        </Typography>
        {sub && (
            <Typography sx={{ fontSize: tokens.font.size.sm, color: tokens.color.textSecondary, marginTop: '3px', fontFamily: tokens.font.family }}>
                {sub}{' '}
                {subAction && (
                    <Box component="span" onClick={subAction.onClick} sx={{ color: tokens.color.textPrimary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                        {subAction.label}
                    </Box>
                )}
            </Typography>
        )}
    </Box>
);

// ── Recent-search dropdown (package-styled, real data) ────────────────────────
const RecentSearches = ({ items, onClick, onRemove, containerRef }) => {
    // Deduplicate by term (case-insensitive), preserving newest-first order.
    const seen = new Set();
    const unique = [];
    for (const it of items) {
        const term = (it.term || '').trim();
        if (!term) continue;
        const key = term.toLowerCase();
        if (!seen.has(key)) {
            seen.add(key);
            unique.push(it);
        }
        if (unique.length >= 3) break;
    }
    // unique currently has newest-first; render newest first
    return (
        <Box ref={containerRef} sx={{ marginTop: '-24px', marginBottom: '32px', background: tokens.color.bgPrimary, border: `0.5px solid ${tokens.color.borderTertiary}`, borderRadius: tokens.radius.md, overflow: 'hidden' }}>
            {unique.map((item, idx) => (
                <Box
                    key={item.id ?? idx}
                    sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', cursor: 'pointer', fontSize: tokens.font.size.md, color: tokens.color.textSecondary, fontFamily: tokens.font.family, '&:hover': { background: tokens.color.bgSecondary } }}
                >
                    <Box onClick={() => onClick(item.term)} sx={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                        <Search sx={{ fontSize: '15px', color: tokens.color.textTertiary }} />
                        {item.term}
                    </Box>
                    <Box component="button" onClick={() => onRemove && onRemove(item)} sx={{ background: 'transparent', border: 'none', color: tokens.color.textTertiary, cursor: 'pointer', padding: 0 }} aria-label={`Remove ${item.term}`}>
                        ×
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

// ── Inline answer card (package visual, real result data) ─────────────────────
// feedbackState: 'idle' | 'yes' | 'no' | 'skipped'. 'Did this answer your question?' is asked once (N4 pt 3.1): a
// reply locks it; after Yes or No the row reads 'Thanks for your feedback.', after Skip it is empty.
// After "No", the card asks 'What were you looking for?' (N4 pt 4.1).
const AnswerCard = ({ result, onSource, onFeedback, feedbackState = 'idle', onSkipFeedback, inlineContentNode = null, hideFeedback = false, onCourseClick, commentQueryId }) => (
    <Box sx={{ marginBottom: '24px' }}>
        <Box sx={{ padding: '24px', borderRadius: tokens.radius.lg, border: `0.5px solid ${tokens.color.borderTertiary}`, background: tokens.color.bgSecondary }}>
            <Box sx={{ marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ fontSize: tokens.font.size.xs, fontWeight: tokens.font.weight.medium, background: tokens.color.textPrimary, color: tokens.color.bgPrimary, padding: '3px 10px', borderRadius: tokens.radius.sm }}>
                    Best answer
                </Box>
                {result.contentType && (
                    <Box sx={{ fontSize: tokens.font.size.xs, fontWeight: tokens.font.weight.medium, background: tokens.color.bgTertiary, color: tokens.color.textSecondary, padding: '3px 10px', borderRadius: tokens.radius.sm }}>
                        {result.contentType.charAt(0).toUpperCase() + result.contentType.slice(1)}
                    </Box>
                )}
            </Box>

            <Typography variant="body1" sx={{ marginBottom: '16px', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: tokens.color.textPrimary }}>
                {result.answer}
            </Typography>

            {result.steps && result.steps.length > 0 && (
                <Box sx={{ marginTop: '16px' }}>
                    <Typography sx={{ fontSize: tokens.font.size.sm, fontWeight: 600, marginBottom: '8px', color: tokens.color.textPrimary }}>
                        Step-by-step
                    </Typography>
                    <Box component="ol" sx={{ paddingLeft: '18px', margin: 0 }}>
                        {result.steps.map((step, index) => (
                            <li key={index}>
                                <Typography variant="body2" sx={{ marginBottom: '4px', color: tokens.color.textPrimary }}>{step}</Typography>
                            </li>
                        ))}
                    </Box>
                </Box>
            )}

            {/* {result.videoUrl && (
                <Box sx={{ marginTop: '24px' }}>
                    <Box sx={{ width: '100%', height: { xs: 180, sm: 260 }, borderRadius: tokens.radius.md, overflow: 'hidden', background: '#000' }}>
                        <vide{/* o width="100%" height="100%" controls src={result.videoUrl} />
                    </Box>
                    <Typography variant="caption" sx={{ marginTop: '8px', display: 'block', color: tokens.color.textSecondary }}>
                        {result.videoTitle || 'Video guide'}
                    </Typography>
                </Box>
            )} */}

            {/* Render inline content player node (if provided) above the feedback question */}
            {inlineContentNode}

            {/* Inline content player is rendered within the LearnerDashboard scope below */}
            <Box
                onClick={onSource}
                sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', cursor: 'pointer', padding: '8px 12px', borderRadius: tokens.radius.sm, transition: `background ${tokens.transition}`, '&:hover': { background: tokens.color.bgTertiary } }}
            >
                <Typography variant="body2" sx={{ fontWeight: 500, color: tokens.color.textPrimary }}>
                    {result.contentTitle}
                </Typography>
                {result.duration && (
                    <Typography variant="body2" sx={{ color: tokens.color.textSecondary }}>
                        ({result.duration} min)
                    </Typography>
                )}
                {/* The page or time the answer comes from (N4 pt 1). Removed 10 Oct 2026 (N4 pt 1): showing pages only. */}
                {seekLabel(result.seek) && (
                    <Typography variant="body2" sx={{ color: tokens.color.textSecondary }}>
                        · {seekLabel(result.seek)}
                    </Typography>
                )}
                {/* 'Part of: [course]' opens the course page (K19.5-cite); stopPropagation keeps the click from also
                    opening the content. Removed 2 Nov 2026 (K19.5-cite): a click handler that only blocked the link,
                    so the link did nothing. */}
                {result.courseName && (
                    <Typography
                        component="a"
                        href={`/course/preview/${result.courseId}`}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            if (onCourseClick) onCourseClick(result.courseId);
                        }}
                        variant="body2"
                        sx={{ color: tokens.color.textSecondary, textDecoration: 'underline' }}
                    >
                        Part of: {result.courseName}
                    </Typography>
                )}
            </Box>

            {/* The row keeps its height once its buttons go, so the page doesn't move at any width (N4 pt 3.1): the
                16px padding, the border, a button's height and the thin scrollbar the row shows when its buttons don't
                fit: the theme's 6px one, and in Firefox, which ignores the theme's, its own thin one. Only Firefox is
                given scrollbarWidth, since Chrome drops the theme's scrollbar wherever it is set. */}
            {!hideFeedback && (
                <Box sx={{ marginTop: '24px', paddingTop: '16px', minHeight: '54px', borderTop: `0.5px solid ${tokens.color.borderTertiary}`, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap', overflowX: 'auto', '@supports (-moz-appearance: none)': { scrollbarWidth: 'thin' } }}>
                    {feedbackState === 'idle' ? (
                        <>
                            {/* Removed 2 Nov 2026 (N4 pt 3.1): 'Did this resolve your question?' */}
                            <Typography variant="body2" sx={{ color: tokens.color.textSecondary, whiteSpace: 'nowrap' }}>
                                Did this answer your question?
                            </Typography>
                            {['Yes', 'No'].map(label => (
                                <Box
                                    key={label}
                                    component="button"
                                    onClick={() => onFeedback(label === 'Yes')}
                                    sx={{
                                        fontSize: tokens.font.size.sm, fontWeight: tokens.font.weight.medium,
                                        color: tokens.color.textPrimary, background: tokens.color.bgPrimary,
                                        border: `0.5px solid ${tokens.color.borderSecondary}`,
                                        borderRadius: tokens.radius.sm, padding: '6px 18px',
                                        cursor: 'pointer', fontFamily: tokens.font.family,
                                        '&:hover': { background: tokens.color.bgSecondary },
                                    }}
                                >
                                    {label}
                                </Box>
                            ))}
                            {onSkipFeedback && (
                                <Box component="button" onClick={onSkipFeedback} sx={{ fontSize: tokens.font.size.sm, color: tokens.color.textSecondary, background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: tokens.font.family, textDecoration: 'underline', padding: '6px 4px' }}>
                                    Skip
                                </Box>
                            )}
                        </>
                    ) : feedbackState === 'yes' || feedbackState === 'no' ? (
                        // Removed 2 Nov 2026 (N4 pt 3.1): after Yes or No an empty row (with its 'glad that resolved it.'
                        // line above the answer after a Yes), and after Skip 'No worries. Search again anytime.'.
                        <Typography variant="body2" sx={{ color: tokens.color.textSecondary }}>
                            Thanks for your feedback.
                        </Typography>
                    ) : null}
                </Box>
            )}
            {/* After a No, straight to 'What were you looking for?' (N4 pts 1.1, 4.1). Removed 2 Nov 2026 (N4 pt 1.1):
                'Not what you were looking for?' with 'Show related modules' and Skip, which led to rounds of related
                answers. */}
            {!hideFeedback && feedbackState === 'no' && (
                <GapComment queryId={commentQueryId} sx={{ fontFamily: tokens.font.family }} />
            )}
        </Box>        
    </Box>
);

// Removed 2 Nov 2026 (N4 pt 1.1): the related answers' rounds after a No (RelatedModulesLoop: 'You might be looking
// for', 'A couple more options', 'Last suggestions', 'None of these'). A No leads straight to 'What were you looking for?'.

// Gap state (spec 2.3): no support links. A search with no answer (N4 pt 2.1): one line on the gap message's pale box
// (K2.3 pt 14.1), shown straight away.
// Removed 10 Oct 2026 (N4 pt 2): 'Try a different search above or browse the learning center. Need help now? Contact
// the team' under the gap message.
// Removed 2 Nov 2026 (N4 pt 1.1): 'gap logged as Exhausted' and 'when the related modules run out' above, with the
// related answers (the No saved when they ran out went with them).
// Removed 2 Nov 2026 (N4 pt 2.1): 'We don't have content for this yet.' here (Try it keeps it), and the box's own hex
// colours (the theme's warnText and warnBg are the same colours).
const GapState = () => (
    <Box sx={{ marginBottom: '32px', background: tokens.color.status.warnBg, border: `0.5px solid ${tokens.color.status.warnText}`, borderRadius: tokens.radius.lg, padding: '24px', textAlign: 'center', color: tokens.color.status.warnText }}>
        <ContentSearchMessage kind="learner-gap" sx={{ fontSize: tokens.font.size.md, color: tokens.color.status.warnText, fontFamily: tokens.font.family }} />
        {/* Removed 2 Nov 2026 (N4 pt 4.1): 'What were you looking for?' at the gap state; it shows only after a No. */}
    </Box>
);

// Removed 2 Nov 2026 (N4 pt 1.1): the skipped state after a No ('No worries. Search again anytime.' in place of the
// answer), with the Skip that led to it.

// ── No-skills onboarding (package visual, real save) ──────────────────────────
const NoSkillsState = ({ onSkillSelect }) => {
    // Real available skills, as the server sends them ({ id, skill_name }): chips are selected and saved by ID
    // (SEAM-1: was hardcoded label strings; saveUserSkills needs IDs).
    const [skills, setSkills] = useState([]);
    const [selected, setSelected] = useState([]); // holds skill IDs
    useEffect(() => {
        userService.getAvailableSkills()
            .then(res => {
                const skillsData = res?.data?.data?.skills;
                setSkills(Array.isArray(skillsData) ? skillsData : []);
            })
            .catch(() => setSkills([]));
    }, []);
    const toggle = (id) => {
        setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
    };
    return (
        <Box sx={{ background: tokens.color.bgPrimary, border: `0.5px solid ${tokens.color.borderTertiary}`, borderRadius: tokens.radius.lg, padding: '28px', textAlign: 'center', marginBottom: '32px' }}>
            <Typography sx={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, color: tokens.color.textPrimary, marginBottom: '6px', fontFamily: tokens.font.family }}>
                {/* American English on every screen (K2.3 pt 17) */}
                Select your skills to get personalized recommendations
            </Typography>
            <Typography sx={{ fontSize: tokens.font.size.md, color: tokens.color.textSecondary, marginBottom: '16px', fontFamily: tokens.font.family }}>
                Choose topics you want to learn about. You can change these anytime.
            </Typography>
            <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {skills.map(skill => (
                    <Box
                        key={skill.id}
                        onClick={() => toggle(skill.id)}
                        sx={{
                            fontSize: tokens.font.size.sm,
                            fontWeight: selected.includes(skill.id) ? tokens.font.weight.medium : tokens.font.weight.regular,
                            color: selected.includes(skill.id) ? tokens.color.bgPrimary : tokens.color.textSecondary,
                            background: selected.includes(skill.id) ? tokens.color.textPrimary : tokens.color.bgPrimary,
                            border: `0.5px solid ${selected.includes(skill.id) ? tokens.color.textPrimary : tokens.color.borderTertiary}`,
                            borderRadius: tokens.radius.sm, padding: '7px 16px', cursor: 'pointer', fontFamily: tokens.font.family,
                            transition: `all ${tokens.transition}`,
                        }}
                    >
                        {/* The skill's name, as the server sends it (N18 pt 2). Removed 2 Nov 2026 (N18 pt 2): skill.name, which
                            it never sends, so every chip was blank. */}
                        {skill.skill_name}
                    </Box>
                ))}
            </Box>
            <Box sx={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <Box
                    component="button"
                    onClick={() => selected.length > 0 && onSkillSelect(selected)}
                    sx={{ fontSize: tokens.font.size.sm, fontWeight: tokens.font.weight.medium, color: selected.length > 0 ? tokens.color.bgPrimary : tokens.color.textTertiary, background: selected.length > 0 ? tokens.color.textPrimary : tokens.color.bgSecondary, border: `0.5px solid ${selected.length > 0 ? tokens.color.textPrimary : tokens.color.borderTertiary}`, borderRadius: tokens.radius.sm, padding: '7px 18px', cursor: selected.length > 0 ? 'pointer' : 'not-allowed', fontFamily: tokens.font.family }}
                >
                    Save and get recommendations
                </Box>
                <Box component="button" onClick={() => onSkillSelect([])} sx={{ fontSize: tokens.font.size.sm, color: tokens.color.textSecondary, background: tokens.color.bgPrimary, border: `0.5px solid ${tokens.color.borderTertiary}`, borderRadius: tokens.radius.sm, padding: '7px 18px', cursor: 'pointer', fontFamily: tokens.font.family, '&:hover': { background: tokens.color.bgSecondary } }}>
                    Skip for now
                </Box>
            </Box>
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component — package visuals, real wiring underneath.
// ─────────────────────────────────────────────────────────────────────────────
const LearnerDashboard = ({ data }) => {
    const [query, setQuery] = useState('');
    // Removed 2 Nov 2026 (N4 pt 1.1): the question that was searched (searchedQuery), which only the related rounds read
    // (K20.1.a pt 9).
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [queryId, setQueryId] = useState(null);
    // The search on screen, for a reply whose request ends after another search started (N4 pt 3.1).
    const currentQueryIdRef = useRef(null);
    useEffect(() => { currentQueryIdRef.current = queryId; }, [queryId]);
    const { openContentPlayer } = useContentPlayer();
    const [inlineContentId, setInlineContentId] = useState(null);
    const [inlineCourseId, setInlineCourseId] = useState(null);
    const [selectedContent, setSelectedContent] = useState(null);
    const [showInlinePlayer, setShowInlinePlayer] = useState(false);
    // The page or time the shown answer cites; the inline player opens there (K20.1.a pt 7).
    const [inlineSeek, setInlineSeek] = useState(null);
    const navigate = useNavigate();

    // ── Search-flow state machine (spec 2.3) ──────────────────────────────────
    // feedbackState: 'idle' | 'yes' | 'no' | 'skipped': locks the feedback after one click
    //   ('Did this answer your question?' is answered once per search, N4 pt 3.1).
    // Removed 2 Nov 2026 (N4 pt 1.1): the flow view ('answer', 'modules', 'gap', 'skipped'), the related answers' rounds,
    // picks and their own Yes / No, and sessionClosed, which only the Skip after a No read.
    const [feedbackState, setFeedbackState] = useState('idle');

    const NO_COVERAGE_MESSAGE = 'The content does not cover this topic.';
    const [skillRecommendation, setSkillRecommendation] = useState([]);
    const [complianceCourses, setComplianceCourses] = useState([]);
    const [recentContentIds, setRecentContentIds] = useState([]); // recently-accessed, for exclusion
    const [quickSkillContent, setQuickSkillContent] = useState(null);

    const [showHistory, setShowHistory] = useState(false);
    const [searchHistory, setSearchHistory] = useState([]);
    const searchHistoryRef = useRef(null);

    const [validationMessage, setValidationMessage] = useState(null);
    const [validationSeverity, setValidationSeverity] = useState(null);
    // A search that failed (K15 pts 4-5): { step: 'search', arg }. Shows the error state, whose Try again runs it again.
    // Removed 2 Nov 2026 (N4 pt 1.1): the steps 'related' and 'pick', with the related answers.
    const [searchFailure, setSearchFailure] = useState(null);

    const { showToast } = useToast();
    const showNotification = (message, severity = 'success') => showToast(message, severity);

    // ── Skills state (fixes "skill selection not working") ────────────────────
    // Previously: hasSkills = data.user.hasSkills ?? (recommendations > 0).
    // Failure modes that made onboarding look broken:
    //   1. After SAVING skills, a user with no matching content still had
    //      0 recommendations → hasSkills stayed false → onboarding re-appeared
    //      as if the save did nothing (it HAD saved).
    //   2. "Skip for now" changed nothing on screen — the prompt just stayed.
    // Fix: ask the real API (userService.hasUserSkills, guarded), whose answer a
    // save on this page also sets, and persist skip per-user so the prompt
    // doesn't nag on every load (it remains reachable via Profile → skills).
    const userId = data?.user?.id ?? 'anon';
    const skipKey = `beetsol_skill_onboarding_skipped_${userId}`;
    const [serverHasSkills, setServerHasSkills] = useState(null); // null = unknown
    // Removed 2 Nov 2026 (N18 pt 1): skillsSavedLocal, a flag of its own for a save on this page (the save now sets the
    // saved state itself).
    const [onboardingSkipped, setOnboardingSkipped] = useState(() => {
        try { return localStorage.getItem(skipKey) === '1'; } catch { return false; }
    });

    useEffect(() => {
        // Guarded: method confirmed in the real api.js per SEAM-2; if absent,
        // serverHasSkills stays null and the page shows no prompt (hasSkills below).
        if (typeof userService.hasUserSkills === 'function') {
            userService.hasUserSkills()
                .then(res => {
                    const d = res?.data;
                    const v = d?.data?.hasSkills ?? d?.hasSkills ?? d?.data ?? null;
                    if (typeof v === 'boolean') setServerHasSkills(v);
                })
                .catch(() => {});
        }
    }, []);

    // The skills prompt reads the saved skills only (N18 pt 1): it shows once /api/users/skills/check says there are
    // none, so it agrees with the profile, wherever the skills were saved or removed. While that is unknown, or if the
    // check fails, the page shows what it shows a learner with skills, with no prompt. K24 pt 5's 'Skip for now' still
    // hides it. Removed 2 Nov 2026 (N18 pt 1): falling back to the sign-in's own flag (data.user.hasSkills) or to whether
    // there were recommendations, which could disagree with the saved skills.
    const hasSkills = serverHasSkills !== false;

    // ── Initial data load (real service calls — unchanged) ────────────────────
    useEffect(() => {
        const fetchRecentSearchesOnMount = async () => {
            try {
                const response = await axios.get('/api/user-queries/recent');
                const json = response.data;
                if (json && json.success && Array.isArray(json.data)) {
                    setSearchHistory(json.data.map(item => ({ term: item.query, id: item.id, timestamp: item.created_at || Date.now() }))
                        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
                } else { setSearchHistory([]); }
            } catch { setSearchHistory([]); }
        };
        fetchRecentSearchesOnMount();

        // Compliance: single real endpoint returns an array of compliance courses.
        // (Old getDueComplianceCourse/getMandatoryCourse were removed from the API.)
        // Fields: id, title, dueDays, progressPercentage, isStarted, completedItems, totalItems.
        // The server sends the list as courses (N40.1 pt 16.1). Removed 2 Nov 2026 (N40.1 pt 16.1): reading data, which
        // the server never sends, so the Required cards never showed.
        learnerService.getComplianceCourses()
            .then(res => {
                const arr = (res?.data?.success ? res.data.courses : null) || [];
                setComplianceCourses(Array.isArray(arr) ? arr : []);
            }).catch(() => setComplianceCourses([]));
        learnerService.getSkillCategoryRecommendation()
            .then(res => setSkillRecommendation(res.data.data || [])).catch(() => setSkillRecommendation([]));
        learnerService.getQuickSkillContent()
            .then(res => {
                const d = res?.data?.data;
                if (Array.isArray(d)) setQuickSkillContent(d.length ? d[0] : null);
                else setQuickSkillContent(d || null);
            }).catch(() => setQuickSkillContent(null));
        // Recently ACCESSED content — used to EXCLUDE already-viewed/completed items
        // it should be fetched here and added as a recommendation SOURCE below.
        learnerService.getRecentContent({ limit: 50 })
            .then(res => setRecentContentIds((res?.data?.data || []).map(it => it.id)))
            .catch(() => setRecentContentIds([]));
    }, []);

    // Removed 10 Oct 2026 (N4 pt 3): the idle (2 min), tab-leave and page-close handlers that posted an
    // automatic No for the current search.

    // ── SAFETY: inline search with validation + re-search detection (unchanged)─
    const handleSearch = async (searchTerm) => {
        const q = (typeof searchTerm === 'string' ? searchTerm : query) || '';
        if (!q.trim()) return;
        // ensure UI reflects chosen term when invoked with an argument
        if (searchTerm) setQuery(q);
        const validation = validateSearchQuery(q);
        if (!validation.isValid) {
            showNotification(validation.message, 'error');
            setResult(null); setQueryId(null);
            return;
        }
        const submittedAt = Date.now();
        setSearchHistory(prev => [
            { term: q, timestamp: submittedAt, queryId: null },
            ...prev
        ]);
        setLoading(true);
        setShowHistory(false);
        // Clear prior search result and inline player state before loading new answer.
        setResult(null);
        setQueryId(null);
        //setSelectedContent(null);
        //setInlineContentId(null);
        //setInlineCourseId(null);
        //setShowInlinePlayer(false);
        // New search = new session: reset the answer-flow state machine.
        setFeedbackState('idle');
        setValidationMessage(null);
        setValidationSeverity(null);
        setSearchFailure(null);
        try {
            const outcome = await searchContent(q);
            if (outcome.kind === 'validation') {
                setValidationMessage(outcome.message);
                setValidationSeverity(outcome.severity);
                setResult(null); setQueryId(null);
                return;
            }
            if (outcome.kind === 'signin') {
                window.location.replace('/login');
                return;
            }
            if (outcome.kind === 'error') {
                setSearchFailure({ step: 'search', arg: q });
                setResult(null); setQueryId(null);
                return;
            }
            const dataRes = outcome.result;
            setResult(dataRes);
            setQueryId(dataRes.queryId);
            setShowInlinePlayer(false);
            // If the search returned a matched content, auto-load it in the inline player
            if (dataRes && dataRes.contentId) {
                const cid = dataRes.contentId;
                const ccourse = dataRes.courseId || null;
                setSelectedContent(null);
                setInlineContentId(null);
                setInlineCourseId(null);
                try {
                    const metaResp = await axios.get(`/api/contents/${cid}`);
                    const c = metaResp?.data?.data || metaResp?.data || null;
                    setSelectedContent(c);
                } catch (err) {
                    console.warn('Failed to fetch content metadata for inline player (auto-load):', err?.response?.data || err.message);
                    setSelectedContent(null);
                }
                setInlineContentId(cid);
                setInlineCourseId(ccourse);
                setInlineSeek(dataRes.seek || null);
                setShowInlinePlayer(true);
            }
            setSearchHistory(prev => {
                const now = Date.now();
                const recentSearches = prev.filter(h => h.timestamp && now - h.timestamp < 10 * 60 * 1000);
                // Removed 10 Oct 2026 (N4 pt 3): marking earlier searches No when the same term was searched
                // more than 3 times in 10 minutes.
                return [{ term: q, timestamp: now, queryId: dataRes.queryId }, ...recentSearches];
            });
        } catch {
            setSearchFailure({ step: 'search', arg: q });
            setResult(null); setQueryId(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchRecentSearches = async () => {
        try {
            const res = await fetch('/api/user-queries/recent');
            const json = await res.json();
            if (json && json.success && Array.isArray(json.data)) {
                setSearchHistory(json.data.map(item => ({ term: item.query, id: item.id, timestamp: item.created_at || Date.now() }))
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
            } else { setSearchHistory([]); }
        } catch (err) {
            console.warn('Failed to fetch recent searches:', err?.response?.data || err.message);
            setSearchHistory([]);
        }
    };

    const handleRemoveRecent = async (item) => {
        try {
            // Optimistically remove from local state
            setSearchHistory(prev => prev.filter(h => (h.term || '').toLowerCase() !== (item.term || '').toLowerCase()));
            // Hide the query in the backend instead of deleting it from the table.
            await axios.post('/api/user-queries/hide', { query: item.term }).catch(() => {});
        } catch (e) {
            console.warn('Failed to remove recent search:', e?.response?.data || e.message);
        }
    };

    // ── SAFETY: content open + pick tracking + enroll-on-access (unchanged) ────
    const handleContentClick = async (contentId, courseId, seek = null) => {
        if (queryId) {
            axios.post('/api/user-queries/pick', { queryId, pickedResult: contentId })
                .catch(err => console.warn('Failed to mark picked result:', err?.response?.data || err.message));
        }
        // Load content metadata and show inline player below the search result
        try {
            const resp = await axios.get(`/api/contents/${contentId}`);
            const c = resp?.data?.data || resp?.data || null;
            setSelectedContent(c);
        } catch (err) {
            console.warn('Failed to fetch content metadata for inline player:', err?.response?.data || err.message);
            setSelectedContent(null);
        }
        setInlineContentId(contentId);
        setInlineCourseId(courseId || null);
        setInlineSeek(seek);
        setShowInlinePlayer(true);
    };

    const closeSearchContentPlayer = () => {
        setShowInlinePlayer(false);
        setInlineContentId(null);
        setInlineCourseId(null);
        setSelectedContent(null);
    };

    const goToCoursePage = (courseId) => { if (courseId) window.location.href = `/course/preview/${courseId}`; };

    const handleReset = () => {
        setQuery(''); setResult(null); setQueryId(null);
        setShowHistory(false); setValidationMessage(null); setValidationSeverity(null);
        setFeedbackState('idle'); setSearchFailure(null);
    };

    // Try again on the error state: run the step that failed once more (K15 pt 4).
    const retrySearchStep = () => {
        const failed = searchFailure;
        setSearchFailure(null);
        if (!failed) return;
        if (failed.step === 'search') handleSearch(failed.arg);
        // Removed 2 Nov 2026 (N4 pt 1.1): retrying a related step (the related list, a related pick).
    };

    // ── Yes / No / Skip on the answer (N4 pts 3, 3.1) ──────────────────────────
    // Yes and No are saved once (/api/user-queries/feedback) and lock; the row then reads 'Thanks for your feedback.'.
    // Only a No marks the search unresolved, and 'What were you looking for?' follows it (N4 pt 4.1).
    // Skip records nothing: the row is left empty and the search stays one with an answer.
    // Removed 2 Nov 2026 (N4 pt 3.1): saving 'skip' (postSkip), which no count reads (the widget saves it until its own
    // change, PR #12), and the feedback handler made for each related answer's card (answerFeedback).
    const handleFeedback = async (isHelpful) => {
        if (feedbackState !== 'idle') return; // once per search
        const forQueryId = queryId;
        setFeedbackState(isHelpful ? 'yes' : 'no');
        try {
            await axios.post('/api/user-queries/feedback', { queryId: forQueryId, intent: isHelpful ? 'yes' : 'no' });
            // Removed 2 Nov 2026 (N4 pt 3.1): the 'glad that resolved it.' line above the answer after a Yes.
        } catch (error) {
            // A reply that wasn't saved isn't given: the buttons come back, as the message asks to try again (N4 pt 3.1).
            // Only while its search is still the one on screen, so a late failure never reopens a newer search's reply.
            if (currentQueryIdRef.current !== forQueryId) return;
            setFeedbackState('idle');
            showNotification('Failed to submit feedback. Please try again.', 'error');
        }
    };

    // Removed 2 Nov 2026 (N4 pt 1.1): the related answers after a No: fetching the related list (POST
    // /api/contents/related-modules, which the widget still calls until PR #12), its rounds, a related pick and its
    // answer (generate-answer), their Yes / No, and the No saved when the rounds ran out (handleGap).

    // Skip on the answer (N4 pt 3.1): records nothing, keeps the answer on screen and leaves the row empty.
    const handleAnswerSkip = () => {
        if (feedbackState !== 'idle') return;
        setFeedbackState('skipped');
    };

    // ── skill save / skip (fixes "skill selection not working") ───────────────
    const handleSkillSelect = async (skillIds) => {
        if (skillIds.length === 0) {
            // Skip for now: hide the prompt (persisted per user) and fall
            // through to the "Get started" content below — previously skip
            // changed nothing on screen.
            setOnboardingSkipped(true);
            try { localStorage.setItem(skipKey, '1'); } catch {}
            return;
        }
        try {
            // SEAM-1 resolved: save by ID via the real userService method.
            await userService.saveUserSkills(skillIds);
            // Flip the page to "Recommended for you" IMMEDIATELY — previously,
            // a saved user with zero matching content was bounced back into
            // the onboarding prompt as if the save had failed.
            setServerHasSkills(true);
            showNotification('Skills saved.', 'success');
            learnerService.getSkillCategoryRecommendation()
                .then(res => setSkillRecommendation(res.data.data || [])).catch(() => {});
        } catch (error) {
            showNotification('Failed to save skills. Please try again.', 'error');
        }
    };

    // ── Map real data into package card shapes ────────────────────────────────
    const isQuickSkillDuplicate = quickSkillContent
        ? skillRecommendation.some(rec => rec.id === quickSkillContent.id) : false;

    // Required training = compliance courses (real getComplianceCourses array).
    // Map to RequiredCard's field vocabulary: id, courseId, name, dueDate, progress.
    const requiredCards = (complianceCourses || []).map(c => ({
        id: `compliance-${c.id}`,
        courseId: c.id,
        name: c.title,
        //dueDate: c.dueDays != null ? `Due in ${c.dueDays} day${c.dueDays !== 1 ? 's' : ''}` : null,
        // On its due day the card reads 'Due today' (N40.1 pt 16.2). Removed 3 Nov 2026 (N40.1 pt 16.2): 'Due in 0 days'.
        dueDate: c.dueDays != null
            ? (c.dueDays < 0
                ? `Overdue by ${Math.abs(c.dueDays)} day${Math.abs(c.dueDays) !== 1 ? 's' : ''}`
                : c.dueDays === 0
                    ? 'Due today'
                    : `Due in ${c.dueDays} day${c.dueDays !== 1 ? 's' : ''}`)
            : null,
        progress: c.progressPercentage || 0,
    }));

    // Only show required training when there are pending required courses
    const pendingRequired = requiredCards.filter(c => (c.progress || 0) < 100);

    // Recommendations. Mandatory is NOT a separate card — it surfaces as a TAG
    // on a recommended item (item.isMandatory). The backend field that flags a
    // mandatory item (is_mandatory) is not yet exposed on these endpoints; until
    // it is, isMandatory resolves false and no tag shows. (Data is tracked.)
    const recommendedModules = [
        ...skillRecommendation.map(content => ({
            id: content.id, courseId: content.course_id, name: content.name,
            duration: content.duration ? `${content.duration} min` : '',
            type: content.type || 'article', skill: content.skill_name || null,
            isMandatory: content.is_mandatory === true,
        })),
        quickSkillContent && !isQuickSkillDuplicate && {
            id: quickSkillContent.id, courseId: quickSkillContent.course_id, name: quickSkillContent.name,
            duration: quickSkillContent.duration ? `${quickSkillContent.duration} min` : '',
            type: quickSkillContent.type || 'article', skill: quickSkillContent.skill_name || null,
            isMandatory: quickSkillContent.is_mandatory === true,
        },
        // backend-pending: a "recently ADDED / unseen content" source belongs here
        // once an endpoint exists. ...recentlyAddedModules,
    ]
        .filter(Boolean)
        // EXCLUDE recently-accessed items so recommendations stay forward-looking.
        .filter(m => !recentContentIds.includes(m.id));

    const skillsList = skillRecommendation.map(s => s.skill_name).filter(Boolean).join(', ');

    const searchBoxRef = useRef(null);

    useEffect(() => {
        const handleOutsideSearchClick = (event) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)
                && searchHistoryRef.current && !searchHistoryRef.current.contains(event.target)) {
                setShowHistory(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideSearchClick);
        return () => document.removeEventListener('mousedown', handleOutsideSearchClick);
    }, []);

    return (
        <Box sx={{ width: '100%', py: '24px' }}>
            <Typography sx={{ fontSize: tokens.font.size.xl, fontWeight: tokens.font.weight.medium, color: tokens.color.textPrimary, marginBottom: '24px', fontFamily: tokens.font.family }}>
                Welcome, {data?.user?.name}.
            </Typography>

            {/* Search bar — primary action, inline */}
            <SearchBar
                containerRef={searchBoxRef}
                value={query}
                onChange={(v) => { setQuery(v); setShowHistory(v.trim().length === 0); }}
                onSubmit={handleSearch}
                onFocus={() => { setShowHistory(true); fetchRecentSearches(); }}
                showClear={!!result}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleSearch(); }
                }}
                onClear={handleReset}
            />
            {loading && (
                <ThinkingIndicator />
            )}
            {/* Recent searches */}
            {showHistory && searchHistory.length > 0 && !result && (
                <RecentSearches containerRef={searchHistoryRef} items={searchHistory} onClick={(term) => { handleSearch(term); }} onRemove={handleRemoveRecent} />
            )}

            {/* A search's validation message (error or warning). Removed 2 Nov 2026 (N4 pt 3.1): the green look for a
                confirmation, whose only use was the 'glad that resolved it.' line after a Yes. */}
            {validationMessage && (
                <Box sx={{ marginBottom: '24px', padding: '12px 16px', borderRadius: tokens.radius.md, border: `0.5px solid ${validationSeverity === 'error' ? tokens.color.alertDot : tokens.color.warning}`, background: validationSeverity === 'error' ? tokens.color.status.alertBg : '#FAEEDA' }}>
                    <Typography sx={{ fontSize: tokens.font.size.md, color: validationSeverity === 'error' ? '#A32D2D' : '#854F0B', fontFamily: tokens.font.family }}>
                        {validationMessage}
                    </Typography>
                </Box>
            )}

            {/* The search failed (K15 pt 4): the error state, never the gap */}
            {searchFailure && searchFailure.step === 'search' && (
                <ContentSearchMessage kind="error" onRetry={retrySearchStep} sx={{ marginBottom: '24px' }} />
            )}

            {/* Search result flow (spec 2.3) OR landing sections */}
            {result && result.answer ? (
                <>
                    {/* Removed 2 Nov 2026 (N4 pt 1.1): the flow views around the answer (the related answers' rounds and picks,
                        the skipped state, the gap state after the rounds) and the error state of a related step. */}
                    <AnswerCard
                        result={result}
                        onSource={() => handleContentClick(result.contentId, result.courseId, result.seek || null)}
                        onFeedback={handleFeedback}
                        feedbackState={feedbackState}
                        onSkipFeedback={handleAnswerSkip}
                        onCourseClick={goToCoursePage}
                        commentQueryId={queryId}
                        hideFeedback={result.answer === NO_COVERAGE_MESSAGE}
                        inlineContentNode={showInlinePlayer && inlineContentId ? (
                            <Box sx={{ marginTop: '20px' }}>
                                {/* <SectionLabel>Content preview</SectionLabel> */}
                                {/* <Box sx={{ padding: '12px', border: `0.5px solid ${tokens.color.borderTertiary}`, borderRadius: tokens.radius.md, background: tokens.color.bgPrimary, marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}> */}
                                    {/* <Box>
                                        <Typography sx={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, color: tokens.color.textPrimary, fontFamily: tokens.font.family }}>
                                            {selectedContent?.name || 'Content'}
                                        </Typography>
                                        <Typography sx={{ fontSize: tokens.font.size.sm, color: tokens.color.textSecondary, marginTop: '4px' }}>
                                            {selectedContent?.duration ? `${selectedContent.duration} min` : (selectedContent?.content_type || '')}
                                            {selectedContent?.course_name ? ` • from ${selectedContent.course_name}` : ''}
                                        </Typography>
                                    </Box> */}
                                    {/* <Box component="button" onClick={() => { setShowInlinePlayer(false); setInlineContentId(null); setSelectedContent(null); }} sx={{ fontSize: tokens.font.size.sm, color: tokens.color.textPrimary, border: `0.5px solid ${tokens.color.borderTertiary}`, background: 'transparent', padding: '8px 12px', borderRadius: tokens.radius.sm, cursor: 'pointer' }}>
                                        Close
                                    </Box> */}
                                {/* </Box> */}
                                <Box>
                                    <ContentPlayer courseId={inlineCourseId} contentId={inlineContentId} seek={inlineSeek} queryId={queryId} isLearnerMode={true} isSearchResult isModal={false} isInline hideCourseNavigation showCloseButton onClose={closeSearchContentPlayer} />
                                </Box>
                            </Box>
                        ) : null}
                    />
                </>
            ) : result && result.message ? (
                // Removed 10 Oct 2026 (N4 pt 2): the first search's own grey box with the server's message. Only a
                // search with no answer reaches the gap state (N4 pt 2.1).
                <GapState />
            ) : (
                <>
                    {/* Required training: show only when pending required courses exist */}
                    {pendingRequired.length > 0 && (
                        <Box sx={{ marginBottom: '32px' }}>
                            <SectionLabel sub="Complete these before the due date">Required training</SectionLabel>
                            {pendingRequired.map(course => (
                                <RequiredCard key={course.id} course={course} onClick={() => goToCoursePage(course.courseId)} />
                            ))}
                        </Box>
                    )}

                    {/* No-skills onboarding — hidden once skipped (persisted).
                        Skills remain editable any time via Profile. */}
                    {!hasSkills && !onboardingSkipped && <NoSkillsState onSkillSelect={handleSkillSelect} />}

                    {/* Skipped onboarding → "Get started" (spec Phase 2 note 3:
                        no skills selected → booster-ordered content under a
                        "Get started" label instead of "Recommended for you").
                        Fed by the existing recommendation endpoints; the
                        dedicated popular-in-workspace query is backend-pending. */}
                    {!hasSkills && onboardingSkipped && (
                        <Box>
                            {/* 'personalized', American English on every screen (K2.3 pt 17) */}
                            <SectionLabel
                                sub="Popular content to explore — or pick skills for personalized recommendations"
                                subAction={{ label: 'Choose skills', onClick: () => { setOnboardingSkipped(false); try { localStorage.removeItem(skipKey); } catch {} } }}
                            >
                                Get started
                            </SectionLabel>
                            {recommendedModules.length > 0 ? (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: '12px' }}>
                                    {recommendedModules.map(module => (
                                        <RecCard key={module.id} module={module} onClick={() => handleContentClick(module.id, module.courseId)} />
                                    ))}
                                </Box>
                            ) : (
                                // The shared empty state, in the gap message's look (K2.3 pt 14.1). Removed 2 Nov 2026 (K2.3
                                // pt 14.1): this box's own grey text on white.
                                <EmptyState title={null} message="Search for anything above to get started." />
                            )}
                        </Box>
                    )}

                    {/* Recommended for you */}
                    {hasSkills && (
                        <Box>
                            <SectionLabel
                                sub={skillsList ? `Based on your skills: ${skillsList}` : null}
                                subAction={skillsList ? { label: 'Edit', onClick: () => { window.location.href = '/users/profile'; } } : null}
                            >
                                Recommended for you
                            </SectionLabel>
                            {recommendedModules.length > 0 ? (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: '12px' }}>
                                    {recommendedModules.map(module => (
                                        module.courseId != null ? (
                                            <RecCard key={module.id} module={module} onClick={() => navigate(`/course/preview/${module.courseId}`)} />
                                        ) : (
                                            <RecCard key={module.id} module={module} onClick={() => handleContentClick(module.id, module.courseId)} />
                                        )
                                    ))}
                                </Box>
                            ) : (
                                // The shared empty state, in the gap message's look (K2.3 pt 14.1). Removed 2 Nov 2026 (K2.3
                                // pt 14.1): this box's own grey text on white.
                                <EmptyState title={null} message="No recommendations yet — search for anything to get started." />
                            )}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default LearnerDashboard;
